import React, { useState, useEffect } from 'react';
import ChartPanel from './PointBasedChart'; // ChartPanel for both trading systems
import { Link } from 'react-router-dom';

export default function PointsBasedTrade() {
  const [trades, setTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(647.61);
  const [tradeAmountUSD, setTradeAmountUSD] = useState('');
  const [totalProfit, setTotalProfit] = useState(0);
  const [totalLoss, setTotalLoss] = useState(0);
  const [walletBalance, setWalletBalance] = useState(1000);
  const [targetPoint, setTargetPoint] = useState('');
  const [stopLossPoint, setStopLossPoint] = useState('');

  useEffect(() => {
    const socket = new WebSocket('wss://stream.binance.com:9443/ws/bnbusdt@trade');
    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      setCurrentPrice(parseFloat(data.p)); // Update current price from WebSocket
    };

    return () => {
      socket.close();
    };
  }, []);

  const calculateAmountOfBNB = (usdAmount) => {
    return usdAmount / currentPrice;
  };

  const handleBuyTrade = () => {
    if (!tradeAmountUSD || !targetPoint || !stopLossPoint) {
      alert("Please fill all fields: Trade amount, target points, and stop loss points.");
      return;
    }

    if (parseFloat(tradeAmountUSD) > walletBalance) {
      alert("Insufficient balance to make this trade!");
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const price = currentPrice;
    const amountBNB = calculateAmountOfBNB(parseFloat(tradeAmountUSD));

    // Calculate the target price for BUY
    const targetPrice = price + parseFloat(targetPoint); // Target for BUY trade
    const stopLossPrice = price - parseFloat(stopLossPoint); // Stop Loss for BUY trade

    const newTrade = {
      type: 'BUY',
      price,
      amountBNB,
      time: now,
      targetPoint: targetPrice, // Calculated target price
      stopLossPoint: stopLossPrice, // Stop Loss price
      profitLoss: 0,
      isClosed: false,
      tradeAmountUSD: parseFloat(tradeAmountUSD),
    };

    setTrades((prevTrades) => [...prevTrades, newTrade]);
    setWalletBalance(walletBalance - parseFloat(tradeAmountUSD)); // Update wallet balance after trade
    setTradeAmountUSD('');
    setTargetPoint('');
    setStopLossPoint('');
  };

  const handleSellTrade = () => {
    if (!tradeAmountUSD || !targetPoint || !stopLossPoint) {
      alert("Please fill all fields: Trade amount, target points, and stop loss points.");
      return;
    }

    if (parseFloat(tradeAmountUSD) > walletBalance) {
      alert("Insufficient balance to make this trade!");
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const price = currentPrice;
    const amountBNB = calculateAmountOfBNB(parseFloat(tradeAmountUSD));

    // Calculate the target price for SELL
    const targetPrice = price - parseFloat(targetPoint); // Target for SELL trade
    const stopLossPrice = price + parseFloat(stopLossPoint); // Stop Loss for SELL trade

    const newTrade = {
      type: 'SELL',
      price,
      amountBNB,
      time: now,
      targetPoint: targetPrice, // Calculated target price
      stopLossPoint: stopLossPrice, // Stop Loss price
      profitLoss: 0,
      isClosed: false,
      tradeAmountUSD: parseFloat(tradeAmountUSD),
    };

    setTrades((prevTrades) => [...prevTrades, newTrade]);
    setWalletBalance(walletBalance - parseFloat(tradeAmountUSD)); // Update wallet balance after trade
    setTradeAmountUSD('');
    setTargetPoint('');
    setStopLossPoint('');
  };

  // Real-time profit/loss calculation every second
  useEffect(() => {
    const interval = setInterval(() => {
      setTrades((prevTrades) =>
        prevTrades.map((trade) => {
          if (!trade.isClosed) {
            let updatedTrade = { ...trade };

            // Real-time Profit/Loss calculation
            if (trade.type === 'BUY') {
              updatedTrade.profitLoss = (currentPrice - trade.price) * trade.amountBNB;
            } else if (trade.type === 'SELL') {
              updatedTrade.profitLoss = (trade.price - currentPrice) * trade.amountBNB;
            }

            // Check if the trade hits the target or stop loss point
            if (currentPrice >= trade.targetPoint && trade.type === 'BUY') {
              updatedTrade.isClosed = true;
              setTotalProfit((prev) => prev + updatedTrade.profitLoss);
              setWalletBalance((prev) => prev + updatedTrade.profitLoss); // Update wallet balance
              setClosedTrades((prev) => [...prev, updatedTrade]); // Record the closed trade
            } else if (currentPrice <= trade.stopLossPoint && trade.type === 'BUY') {
              updatedTrade.isClosed = true;
              setTotalLoss((prev) => prev + Math.abs(updatedTrade.profitLoss));
              setWalletBalance((prev) => prev + updatedTrade.profitLoss); // Update wallet balance
              setClosedTrades((prev) => [...prev, updatedTrade]); // Record the closed trade
            }

            if (currentPrice <= trade.targetPoint && trade.type === 'SELL') {
              updatedTrade.isClosed = true;
              setTotalProfit((prev) => prev + updatedTrade.profitLoss);
              setWalletBalance((prev) => prev + updatedTrade.profitLoss); // Update wallet balance
              setClosedTrades((prev) => [...prev, updatedTrade]); // Record the closed trade
            } else if (currentPrice >= trade.stopLossPoint && trade.type === 'SELL') {
              updatedTrade.isClosed = true;
              setTotalLoss((prev) => prev + Math.abs(updatedTrade.profitLoss));
              setWalletBalance((prev) => prev + updatedTrade.profitLoss); // Update wallet balance
              setClosedTrades((prev) => [...prev, updatedTrade]); // Record the closed trade
            }

            return updatedTrade;
          }
          return trade;
        })
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [currentPrice]);

  return (
    <div
      style={{
        backgroundColor: '#000',
        color: '#fff',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
      }}
    >
      <h2 style={{ textAlign: 'center' }}>Points-Based Trading</h2>

      {/* Navigation button to switch to Time-Based Trading */}
      <div style={{ marginBottom: '20px' }}>
        <Link to="/">
          <button
            style={{
              background: 'blue',
              color: '#fff',
              padding: '12px 24px',
              fontSize: '16px',
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Go to Time-Based Trading
          </button>
        </Link>
      </div>

      <ChartPanel trades={trades} setCurrentPrice={setCurrentPrice} /> {/* Include ChartPanel here */}

      {/* Target Point and Stop Loss Inputs */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ color: '#fff' }}>Enter Target Points: </label>
        <input
          type="number"
          value={targetPoint}
          onChange={(e) => setTargetPoint(e.target.value)}
          placeholder="Enter target price points"
          style={{
            padding: '5px 10px',
            margin: '0 10px',
            borderRadius: '4px',
            border: '1px solid #fff',
            color: '#000',
          }}
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ color: '#fff' }}>Enter Stop Loss Points: </label>
        <input
          type="number"
          value={stopLossPoint}
          onChange={(e) => setStopLossPoint(e.target.value)}
          placeholder="Enter stop loss price points"
          style={{
            padding: '5px 10px',
            margin: '0 10px',
            borderRadius: '4px',
            border: '1px solid #fff',
            color: '#000',
          }}
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ color: '#fff' }}>Enter USD Amount for Trade: </label>
        <input
          type="number"
          value={tradeAmountUSD}
          onChange={(e) => setTradeAmountUSD(e.target.value)}
          placeholder="Enter amount"
          style={{
            padding: '5px 10px',
            margin: '0 10px',
            borderRadius: '4px',
            border: '1px solid #fff',
            color: '#000',
          }}
        />
        <span style={{ color: '#fff' }}>USD</span>
      </div>

      {/* BUY/SELL buttons */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
        <button
          onClick={handleBuyTrade}
          style={{
            background: 'green',
            color: '#fff',
            padding: '12px 24px',
            fontSize: '16px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
          }}
        >
          BUY ↑
        </button>
        <button
          onClick={handleSellTrade}
          style={{
            background: 'red',
            color: '#fff',
            padding: '12px 24px',
            fontSize: '16px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
          }}
        >
          SELL ↓
        </button>
      </div>

      {/* Open Positions Section */}
      <div style={{ width: '100%', maxWidth: '900px', background: '#111', padding: 15, borderRadius: 6 }}>
        <h3 style={{ marginBottom: 10 }}>Open Positions</h3>
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
          {trades.map((trade, i) => (
            <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid #333' }}>
              {new Date(trade.time * 1000).toLocaleTimeString()} —
              <b>{trade.type}</b> @ ${trade.price} —
              Amount: ${trade.tradeAmountUSD} —
              Target: ${trade.targetPoint} (Calculated from entry price) —
              Stop Loss: ${trade.stopLossPoint}
              <div style={{ color: trade.profitLoss > 0 ? 'green' : 'red', marginTop: '5px' }}>
                Profit/Loss: ${trade.profitLoss !== null ? trade.profitLoss.toFixed(2) : 'N/A'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Closed Positions Section */}
      <div style={{ width: '100%', maxWidth: '900px', background: '#111', padding: 15, borderRadius: 6 }}>
        <h3 style={{ marginBottom: 10 }}>Closed Positions</h3>
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
          {closedTrades.map((trade, i) => (
            <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid #333' }}>
              {new Date(trade.time * 1000).toLocaleTimeString()} —
              <b>{trade.type}</b> @ ${trade.price} —
              Amount: ${trade.tradeAmountUSD} —
              Closed at {new Date(trade.closeTime * 1000).toLocaleTimeString()} @ ${trade.closePrice} —
              Profit/Loss: ${trade.profitLoss.toFixed(2)}
            </div>
          ))}
        </div>
      </div>

      {/* Total Profit, Loss, and Wallet Balance */}
      <div style={{
        marginTop: '20px',
        color: '#fff',
        fontSize: '16px',
        padding: '10px 0',
        backgroundColor: '#333',
        borderRadius: '6px',
        width: '100%',
        maxWidth: '900px',
        textAlign: 'center'
      }}>
        <h3>Total Profit: ${totalProfit.toFixed(2)}</h3>
      </div>

      <div style={{
        marginTop: '20px',
        color: '#fff',
        fontSize: '16px',
        padding: '10px 0',
        backgroundColor: '#333',
        borderRadius: '6px',
        width: '100%',
        maxWidth: '900px',
        textAlign: 'center'
      }}>
        <h3>Total Loss: ${totalLoss.toFixed(2)}</h3>
      </div>

      <div style={{
        marginTop: '20px',
        color: '#fff',
        fontSize: '16px',
        padding: '10px 0',
        backgroundColor: '#333',
        borderRadius: '6px',
        width: '100%',
        maxWidth: '900px',
        textAlign: 'center'
      }}>
        <h3>Wallet Balance: ${walletBalance.toFixed(2)}</h3>
      </div>
    </div>
  );
}
